import { useEffect, useRef, useState } from 'react'
import type { DashboardWidget } from './widgets'

/**
 * The widgets that are switched off, and a way to put one back.
 *
 * A menu rather than the checkbox list this used to be. The list showed every widget whether it was
 * on the board or not, so switching one off meant finding its row among eleven - while the panel it
 * referred to was somewhere else on the screen entirely. Hiding now happens on the panel itself,
 * which leaves this with the one job a panel cannot do for itself: bringing back something that has
 * no panel on the board to carry a control.
 *
 * It says so when there is nothing left to add, rather than opening on an empty box. A menu that can
 * be empty and does not say so is a control somebody clicks twice to find out whether it is broken.
 *
 * A popover, not a dialog: it holds one list and picking from it is one click, so trapping focus for
 * that is a heavier thing than the job. The dismissal behavior is `AccountMenu`'s, which is the only
 * other popover in this console.
 */
export function AddWidgetMenu({
  hidden,
  onAdd
}: {
  hidden: readonly DashboardWidget[]
  onAdd: (widget: string) => void
}) {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    // pointerdown rather than click, so a press that begins outside closes the menu before whatever
    // is under it receives the release.
    const onOutside = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }

    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onOutside)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onOutside)
    }
  }, [open])

  return (
    <div className="add-widget" ref={container}>
      <button
        type="button"
        aria-expanded={open}
        disabled={hidden.length === 0}
        onClick={() => setOpen((showing) => !showing)}
        data-testid="add-widget"
      >
        {hidden.length === 0 ? 'Everything is on the board' : `Add a panel (${hidden.length})`}
      </button>

      {open && (
        <ul className="add-widget-menu" data-testid="add-widget-menu">
          {hidden.map((definition) => (
            <li key={definition.id}>
              <button
                type="button"
                onClick={() => {
                  onAdd(definition.id)
                  setOpen(false)
                }}
                data-testid={`add-widget-${definition.id}`}
              >
                <span className="widget-name">{definition.title}</span>
                <span className="hint">{definition.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
